import moment from 'moment';

import { setUrls, urls } from 'Utilities/dataHelpers';
import * as log from 'Utilities/log';
import mediator from 'Utilities/mediator';
import {
  clearCookie,
  clearSession,
  getSessionObject,
  setSessionObject,
  updateChosenCountryLang,
} from 'Utilities/storage';
import { store } from 'Utilities/store';

const _doLoginCheck = () => {
  const {
    auth: { loggedIn },
  } = store.getState();

  if (!loggedIn) {
    log.warn('You must be logged in to perform this request');
  }

  return loggedIn;
};

const _handle2fa = () => {
  return new Promise<LVApiResponse>((resolve, reject) => {
    const code = window.prompt('Enter 2FA code', '');

    if (code === null || code === '') {
      reject();
      return;
    }

    mediator
      .request('api:invoke', {
        method: 'POST',
        url: urls.lslApi + 'auth/continue/2fa/result',
        data: {
          code,
          isPrimaryMethod: true,
        },
      })
      .then((result) => {
        if (result.status === 0) {
          resolve(result);
        }

        reject();
      })
      .catch(() => {
        reject();
      });
  });
};

const _handle2faRequestCode = () => {
  return new Promise<LVApiResponse>((resolve, reject) => {
    mediator
      .request('api:invoke', {
        method: 'POST',
        url: urls.lslApi + 'auth/continue/2fa/sendcode',
        data: {
          isPrimaryMethod: true,
        },
      })
      .then((result) => {
        if (result.status === 0) {
          resolve(result);
        }

        reject();
      })
      .catch(() => {
        reject();
      });
  });
};

const _doLogin = (request: any) => {
  return (request.response = new Promise<LVApiResponse>((resolve, reject) => {
    mediator
      .request<any>('api:invoke', {
        method: 'POST',
        url: urls.lslApi + 'auth/login',
        data: {
          email: request.email,
          password: request.password,
          fingerprint: '',
        },
      })
      .then((result) => {
        if (result.status === 0) {
          if (result.data && result.data.redirect) {
            const { regionalMap } = store.getState().config;

            const regionalUrls = regionalMap[result.data.region];

            if (regionalUrls) {
              setUrls(regionalUrls);

              // login again with the new url
              _doLogin(request);
              request.response
                .then((result: any) => resolve(result))
                .catch((err: any) => reject(err));
              return;
            }

            // TODO: bad state, lvis telling us wrong region
            // but no way to get there
          }

          mediator.publish('user:loginSuccess', result.data);
        } else if ([1, 2, 3].includes(result.status)) {
          log.error(result.error);
          reject(result.error);
          return;
        } else if (result.status === 4 && result.data.step.type === '2faVerify') {
          let p = Promise.resolve<LVApiResponse>({ status: 0, data: {} });

          if (result.data.step.props.primaryMethod) {
            p = _handle2faRequestCode();
          }

          p.then(() => {
            _handle2fa()
              .then((result) => {
                mediator.publish('user:loginSuccess', result.data);
                mediator.publish('router:navigate', '/');
                resolve(result);
              })
              .catch(() => reject());
          });

          return;
        }

        resolve(result);
      })
      .catch((err) => {
        log.error(err);
        mediator.publish('error:display', {
          error: {
            code: err.status,
            type: 'ERROR_AUTH_LOGIN',
          },
        });
        reject(err);
      });
  }));
};

/** Behind the scenes login helper */
const login = () => {
  const email = window.prompt('Email', getSessionObject('email') || '');
  const password = window.prompt('Password', getSessionObject('password') || '');

  if (email && password) {
    _doLogin({ email, password }).then((result) => {
      if (result.status === 0) {
        if (result.data.user.accountType !== 'pat') {
          log.warn('Only patient accounts can be logged into ll-webviews');
          clearSession();
          return;
        }

        updateChosenCountryLang(result.data.user.country, result.data.user.uiLanguage);
        setSessionObject('email', email);
        setSessionObject('password', password);
      }
    });
  }
};

const logout = () => {
  clearSession();
  clearCookie('token');
  clearCookie('loggedIn');

  mediator.publish('router:reload');
};

const setLanguage = (lang?: string) => {
  if (lang) {
    mediator.publish('trans:change', lang);
  }
};

const setToken = () => {
  const token = window.prompt('User Token', '');

  setSessionObject('loggedIn', true);
  setSessionObject('token', {
    token,
    expires: moment()
      .add(5, 'minutes')
      .unix(),
  });

  window.location.reload();
};

const setUrlsRegion = () => {
  let region = window.prompt('Region', '');

  if (!region) {
    region = 'us';
  }

  const { regionalMap } = store.getState().config;

  const regionalUrls = regionalMap[region];

  if (regionalUrls) {
    setUrls(regionalUrls);
  }
};

/** Allows testers to create a connection between the current patient and the supplied Redox partner (partner.Test must be enabled). */
const createRedoxConnection = () => {
  if (!_doLoginCheck()) {
    return;
  }

  const partnerID = window.prompt('Please enter the Redox partner ID');

  mediator
    .request('api:invoke', {
      method: 'POST',
      url: urls.lslApi + 'testing/redox/createconnection',
      data: { partnerID, useCaller: true },
    })
    .then((result) => {
      if (result.status === 0) {
        log.info(`Successfully submited the connection order`);
      } else {
        log.error('Request failed.', result);
      }
    })
    .catch((err) => {
      log.error('Failed to request to create connection.', err);
    });
};

/** Allows testers to create a connection between a new patient and the supplied Redox partner (partner.Test must be enabled). */
const sendRedoxInvite = () => {
  if (!_doLoginCheck()) {
    return;
  }

  const partnerID = window.prompt('Please enter the Redox partner ID');
  const firstName = window.prompt('Please enter the patient first name');
  const lastName = window.prompt('Please enter the patient last name');
  const dob = window.prompt('Please enter the patient DOB (YYYY-MM-DD)');
  const email = window.prompt('Please enter the patient email');

  mediator
    .request('api:invoke', {
      method: 'POST',
      url: urls.lslApi + 'testing/redox/createconnection',
      data: { partnerID, firstName, lastName, dob, email, useCaller: false },
    })
    .then((result) => {
      if (result.status === 0) {
        log.info(`Successfully submited the connection order`);
      } else {
        log.error('Request failed.', result);
      }
    })
    .catch((err) => {
      log.error('Failed to request to create connection.', err);
    });
};

/** Allows testers to create a connection between the current patient and the supplied Fhir partner (partner.Test must be enabled). */
const createFhirConnection = () => {
  if (!_doLoginCheck()) {
    return;
  }

  const partnerID = window.prompt('Please enter the Fhir partner ID');
  const productName = window.prompt('Please enter the Fhir product name');

  mediator
    .request('api:invoke', {
      method: 'POST',
      url: urls.lslApi + 'testing/fhir/createconnection',
      data: { partnerID, productName, useCaller: true },
    })
    .then((result) => {
      if (result.status === 0) {
        log.info(
          `Successfully created the connection with fhir partner \nPlease refresh the page to reflect the changes`
        );
      } else {
        log.error('Request failed.', result);
      }
    })
    .catch((err) => {
      log.error('Failed to request to create connection.', err);
    });
};

declare global {
  interface Window {
    testHelper: {
      login: typeof login;
      logout: typeof logout;
      setLanguage: typeof setLanguage;
      setToken: typeof setToken;
      setUrlsRegion: typeof setUrlsRegion;
      createRedoxConnection: typeof createRedoxConnection;
      sendRedoxInvite: typeof sendRedoxInvite;
      createFhirConnection: typeof createFhirConnection;
    };
  }
}

window.testHelper = {
  // appNavigate,
  // printState,
  login,
  logout,
  setLanguage,
  setToken,
  setUrlsRegion,
  createRedoxConnection,
  sendRedoxInvite,
  createFhirConnection,
};
